import React, { useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, Play, RefreshCw } from 'lucide-react';
import closeIntelApi from './closeIntelApi';
import SectionGuide from './SectionGuide';
import { formatAccounting } from './boardPackHelpers';
import {
  bridgeCoverageLine,
  buildNarrativeQaRows,
  guardSummaryStrip,
  guardTone,
  scopeLabel,
} from './closeRunHelpers';

const REVIEW_BASIS = 'budget';

const previousMonth = () => {
  const date = new Date();
  date.setMonth(date.getMonth() - 1);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
};

const asApiPeriod = (month) => (String(month || '').length === 7 ? `${month}-01` : month || undefined);

const fmt = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n.toLocaleString() : '0';
};

const errorText = (error) => {
  const detail = error?.response?.data?.error ?? error?.message ?? error;
  return typeof detail === 'string' ? detail : JSON.stringify(detail);
};

const runId = (run) => run.id || run.runId || run.closeRunId || `${run.kind}-${run.asOfPeriod}-${run.startedAt}`;

const runPeriod = (run) => String(run.asOfPeriod || run.as_of_period || '').slice(0, 7);

const firstNumeric = (obj) => {
  if (!obj || typeof obj !== 'object') return undefined;
  return Object.values(obj).find((value) => Number.isFinite(Number(value)));
};

export const nextProgress = (state = {}, response = {}) => {
  const narratives = response?.narratives || {};
  const rawRemaining = narratives.remaining ?? state.remaining;
  const remaining = Number.isFinite(Number(rawRemaining)) ? Math.max(0, Number(rawRemaining)) : undefined;
  const itemCount = Array.isArray(narratives.items) ? narratives.items.length : undefined;
  const priorRemaining = Number.isFinite(Number(state.remaining)) ? Number(state.remaining) : undefined;
  const inferredCount =
    priorRemaining !== undefined && remaining !== undefined ? Math.max(0, priorRemaining - remaining) : 0;
  const writtenNow = itemCount !== undefined ? itemCount : inferredCount;
  const status = response.status || (remaining === 0 ? 'completed' : state.status || 'running');
  return {
    runId: response.closeRunId || response.id || state.runId,
    status,
    written: Math.max(0, Number(state.written || 0) + writtenNow),
    remaining,
    guardPass: narratives.guardPass ?? response.guardPass ?? state.guardPass,
  };
};

const progressText = (progress) => {
  if (!progress) return '';
  if (progress.status === 'completed' || progress.remaining === 0) {
    const guard =
      progress.guardPass === false ? 'fail' : progress.guardPass === true ? 'pass' : 'pending';
    return `Close completed - guard ${guard}`;
  }
  return `Narrating - ${fmt(progress.written)} written · ${fmt(progress.remaining)} remaining`;
};

function RunChain({ run }) {
  const bridges =
    run.nBridges ?? run.n_bridges ?? run.bridges?.n ?? run.bridges?.count ?? run.bridges?.nBridges ??
    run.bridges?.n_bridges ?? run.counts?.nBridges ?? firstNumeric(run.bridges);
  const narratives =
    run.nNarratives ?? run.n_narratives ?? run.narratives?.n ?? run.narratives?.count ??
    run.counts?.nNarratives ?? firstNumeric(run.narratives);
  const guard = run.guardPass ?? run.guard_pass ?? run.narratives?.guardPass;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 8 }}>
      <div style={{ border: '1px solid var(--ace-line)', borderRadius: 14, padding: 10 }}>
        <div className="ace-eyebrow">Bridges computed</div>
        <strong>{fmt(bridges)}</strong>
      </div>
      <div style={{ border: '1px solid var(--ace-line)', borderRadius: 14, padding: 10 }}>
        <div className="ace-eyebrow">Narratives</div>
        <strong>{fmt(narratives)}</strong>
      </div>
      <div style={{ border: '1px solid var(--ace-line)', borderRadius: 14, padding: 10 }}>
        <div className="ace-eyebrow">Guard</div>
        <strong style={{ color: guard === false ? 'var(--ace-negative)' : 'var(--ace-positive)' }}>
          {guard === false ? 'Fail' : guard === true ? 'Pass' : 'Pending'}
        </strong>
      </div>
    </div>
  );
}

function NarrativeQaRow({ row }) {
  const [expanded, setExpanded] = useState(false);
  const narrative = row.narrative;
  const guard = narrative?.guardPass;
  const tone = guardTone(guard);
  const variance = Number(row.variance);
  const varianceClass = Number.isFinite(variance) && variance < 0 ? 'bp-negative' : '';
  const body = narrative?.body || '';
  const clamped = body.length > 160 && !expanded;

  return (
    <div className="ci-close-qa-row">
      <div className="ci-close-qa-row-head">
        <strong className="ci-close-qa-scope">{scopeLabel(row)}</strong>
        <span className={`bp-mono bp-amount ${varianceClass}`}>{formatAccounting(row.variance)}</span>
        <span className={`bp-guard-badge bp-guard-badge--${tone}`}>
          {tone === 'pass' ? 'PASS' : tone === 'fail' ? 'FAIL' : 'PENDING'}
        </span>
        {narrative?.model && <span className="ci-close-qa-model">{narrative.model}</span>}
      </div>
      {narrative == null ? (
        <p className="bp-muted" style={{ margin: '6px 0 0' }}>
          No narrative (below materiality or pending)
        </p>
      ) : (
        <>
          <p className={`ci-close-qa-body${clamped ? ' ci-close-qa-body--clamp' : ''}`}>{body}</p>
          {body.length > 160 && (
            <button type="button" className="bp-inline-link" onClick={() => setExpanded((open) => !open)}>
              {expanded ? 'SHOW LESS' : 'SHOW FULL'}
            </button>
          )}
        </>
      )}
    </div>
  );
}

function CloseRunReviewPane({ board, error, loading, onOpenBoard }) {
  if (loading) return <div className="ci-progress-sweep" aria-hidden />;
  if (error) return <p className="ci-error" style={{ margin: '10px 0 0' }}>{error}</p>;
  if (!board) return null;

  const rows = buildNarrativeQaRows(board);

  return (
    <div className="ci-close-qa-pane">
      <p className="ci-mono-strip">{guardSummaryStrip(board, REVIEW_BASIS)}</p>
      <p className="ci-mono-strip" style={{ marginTop: 4 }}>{bridgeCoverageLine(board.lines)}</p>
      <div className="ci-close-qa-list">
        {rows.map((row) => (
          <NarrativeQaRow key={row.key} row={row} />
        ))}
      </div>
      <button
        type="button"
        className="ace-pill ci-pinpoint-button ci-tip"
        data-tip="Open this run in the board pack"
        onClick={onOpenBoard}
        style={{ marginTop: 12 }}
      >
        OPEN BOARD PACK →
      </button>
    </div>
  );
}

export default function CloseRunPage({ navigateTab }) {
  const [asOfPeriod, setAsOfPeriod] = useState(previousMonth());
  const [dryRun, setDryRun] = useState(true);
  const [runs, setRuns] = useState([]);
  const [lastResult, setLastResult] = useState(null);
  const [narrationProgress, setNarrationProgress] = useState(null);
  const [busy, setBusy] = useState(false);
  const [continuingRunId, setContinuingRunId] = useState('');
  const [error, setError] = useState('');
  const [expandedRunId, setExpandedRunId] = useState('');
  const [boardCache, setBoardCache] = useState({});
  const [boardLoading, setBoardLoading] = useState({});
  const [boardErrors, setBoardErrors] = useState({});

  const loadRuns = async () => {
    setError('');
    try {
      const payload = await closeIntelApi.listRuns();
      setRuns(payload?.runs || []);
    } catch (e) {
      setError(errorText(e));
    }
  };

  useEffect(() => {
    loadRuns();
  }, []);

  const closeRuns = useMemo(() => runs.filter((run) => run.kind === 'close'), [runs]);
  const monitorRuns = useMemo(() => runs.filter((run) => run.kind === 'monitor'), [runs]);

  const loadBoard = async (id) => {
    if (!id || boardCache[id] || boardLoading[id]) return;
    setBoardLoading((current) => ({ ...current, [id]: true }));
    setBoardErrors((current) => ({ ...current, [id]: '' }));
    try {
      const payload = await closeIntelApi.board(id, { basis: REVIEW_BASIS });
      setBoardCache((current) => ({ ...current, [id]: payload }));
    } catch (e) {
      setBoardErrors((current) => ({ ...current, [id]: errorText(e) }));
    } finally {
      setBoardLoading((current) => ({ ...current, [id]: false }));
    }
  };

  const toggleReview = (id) => {
    if (expandedRunId === id) {
      setExpandedRunId('');
      return;
    }
    setExpandedRunId(id);
    loadBoard(id);
  };

  const continueNarration = async (id, initialResponse = null) => {
    let progress = nextProgress({ runId: id, written: 0 }, initialResponse || { closeRunId: id, status: 'running' });
    setNarrationProgress(progress);
    let guard = 0;
    while (progress.remaining !== 0 && guard < 100) {
      guard += 1;
      const response = await closeIntelApi.continueClose(id, { limit: 2, dryRun });
      progress = nextProgress(progress, response);
      setNarrationProgress(progress);
      setLastResult(response);
      if (response.status === 'completed' || progress.remaining === 0) break;
    }
    await loadRuns();
  };

  const runClose = async () => {
    setBusy(true);
    setError('');
    setNarrationProgress(null);
    try {
      const result = await closeIntelApi.runClose({ asOfPeriod: asApiPeriod(asOfPeriod), dryRun, maxNarratives: 0 });
      setLastResult(result);
      const id = result?.closeRunId || result?.id;
      if (id && (result.status === 'running' || Number(result?.narratives?.remaining || 0) > 0)) {
        await continueNarration(id, result);
      } else {
        setNarrationProgress(nextProgress({ runId: id, written: 0 }, result));
        await loadRuns();
      }
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  const resumeNarration = async (run) => {
    const id = runId(run);
    setContinuingRunId(id);
    setError('');
    setNarrationProgress(null);
    try {
      await continueNarration(id);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setContinuingRunId('');
    }
  };

  const openBoardPack = (id) => {
    if (!navigateTab || !id) return;
    navigateTab('board', { run: id });
  };

  const openMonitorFindings = (run) => {
    if (!navigateTab) return;
    const period = runPeriod(run);
    navigateTab('monitor', period ? { period } : {});
  };

  return (
    <section className="ace-panel" style={{ padding: 18, borderRadius: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
        <div>
          <div className="ace-eyebrow">CI_04 <span aria-hidden>●</span> Close run</div>
          <h2 style={{ margin: '5px 0 0' }}>Bridge to narrative run log</h2>
        <SectionGuide guideId="close" />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <input
            className="ace-input ci-tip"
            data-tip="Close period to narrate"
            type="month"
            value={asOfPeriod}
            onChange={(event) => setAsOfPeriod(event.target.value)}
            style={{ width: 150 }}
          />
          <label className="ace-pill ci-tip" data-tip="Nothing is persisted as published; safe to test" style={{ cursor: 'pointer' }}>
            <input type="checkbox" checked={dryRun} onChange={(event) => setDryRun(event.target.checked)} />
            Dry run
          </label>
          <button
            type="button"
            className="ace-pill ci-tip ci-pinpoint-button"
            data-tip="Compute deterministic bridges, then narrate scope-by-scope — resumable"
            disabled={busy || Boolean(continuingRunId)}
            onClick={runClose}
          >
            {busy ? <RefreshCw size={13} /> : <Play size={13} />} Run close
          </button>
        </div>
      </div>

      {error && <p style={{ color: 'var(--ace-negative)' }}>{error}</p>}
      {narrationProgress && (
        <p style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 11, color: 'hsl(var(--muted-foreground))' }}>
          {progressText(narrationProgress)}
        </p>
      )}
      {lastResult && (
        <div style={{ marginBottom: 16 }}>
          <div className="ace-eyebrow">Latest result</div>
          <RunChain run={lastResult} />
        </div>
      )}

      <div style={{ display: 'grid', gap: 16 }}>
        <section style={{ border: '1px solid var(--ace-line)', borderRadius: 14, overflow: 'hidden' }}>
          <div style={{ padding: 12, borderBottom: '1px solid var(--ace-line)' }}>
            <div className="ace-eyebrow">Close runs</div>
          </div>
          {closeRuns.map((run) => {
            const id = runId(run);
            const isExpanded = expandedRunId === id;
            return (
              <div key={id} style={{ borderBottom: '1px solid var(--ace-line)' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '220px minmax(0, 1fr)', gap: 12, padding: 12 }}>
                  <div>
                    <strong>{run.asOfPeriod || run.as_of_period || 'Period n/a'}</strong>
                    <small style={{ display: 'block', color: 'hsl(var(--muted-foreground))' }}>
                      {run.status || 'status n/a'} · {run.startedAt || run.started_at || 'not started'}
                    </small>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
                      {run.status === 'running' && (
                        <button
                          type="button"
                          className="ace-pill ci-tip"
                          data-tip="Resume narrating pending scopes for this run"
                          disabled={Boolean(continuingRunId) || busy}
                          onClick={() => resumeNarration(run)}
                          style={{ fontSize: 11, padding: '4px 8px' }}
                        >
                          {continuingRunId === id ? 'Continuing' : 'Continue narration'}
                        </button>
                      )}
                      <button
                        type="button"
                        className={`ace-pill ci-tip${isExpanded ? ' ace-pill--active' : ''}`}
                        data-tip="Inspect every narrative and its guard status"
                        onClick={() => toggleReview(id)}
                        style={{ fontSize: 11, padding: '4px 8px' }}
                      >
                        {isExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                        REVIEW
                      </button>
                    </div>
                  </div>
                  <RunChain run={run} />
                </div>
                {isExpanded && (
                  <div style={{ padding: '0 12px 12px 232px' }}>
                    <CloseRunReviewPane
                      board={boardCache[id]}
                      error={boardErrors[id]}
                      loading={boardLoading[id]}
                      onOpenBoard={() => openBoardPack(id)}
                    />
                  </div>
                )}
              </div>
            );
          })}
          {!closeRuns.length && (
            <p style={{ margin: 0, padding: 12, color: 'hsl(var(--muted-foreground))' }}>No close runs returned.</p>
          )}
        </section>

        <section style={{ border: '1px solid var(--ace-line)', borderRadius: 14, overflow: 'hidden' }}>
          <div style={{ padding: 12, borderBottom: '1px solid var(--ace-line)' }}>
            <div className="ace-eyebrow">Monitor runs feeding close</div>
          </div>
          {monitorRuns.slice(0, 8).map((run) => (
            <div
              key={runId(run)}
              style={{ display: 'grid', gridTemplateColumns: '220px minmax(0, 1fr)', gap: 12, padding: 12, borderBottom: '1px solid var(--ace-line)' }}
            >
              <div>
                <strong>{run.asOfPeriod || run.as_of_period || 'Period n/a'}</strong>
                <small style={{ display: 'block', color: 'hsl(var(--muted-foreground))' }}>{run.status || 'status n/a'}</small>
                <button
                  type="button"
                  className="bp-inline-link"
                  onClick={() => openMonitorFindings(run)}
                  style={{ marginTop: 8 }}
                >
                  REVIEW FINDINGS →
                </button>
              </div>
              <RunChain run={run} />
            </div>
          ))}
          {!monitorRuns.length && (
            <p style={{ margin: 0, padding: 12, color: 'hsl(var(--muted-foreground))' }}>No monitor runs returned.</p>
          )}
        </section>
      </div>
    </section>
  );
}
